import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth'
import { auth } from './firebase'
import { api } from './api'

export interface AuthUser {
  uid: string
  email: string | null
  displayName: string | null
  photoURL: string | null
}

class AuthService {
  private googleProvider = new GoogleAuthProvider()

  async signUp(email: string, password: string, displayName?: string): Promise<AuthUser> {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password)

      if (displayName) {
        await updateProfile(user, { displayName })
      }

      // Ensure user exists in backend
      await this.ensureUserInBackend(user)

      return this.mapUser(user)
    } catch (error) {
      //console.error('❌ Email signup failed:', error);
      //console.error('📋 Error details:', {
      //   code: (error as any)?.code,
      //   message: (error as any)?.message,
      //   email: email
      // });
      throw error
    }
  }

  async signIn(email: string, password: string): Promise<AuthUser> {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password)

      // Ensure user exists in backend
      await this.ensureUserInBackend(user)

      return this.mapUser(user)
    } catch (error: any) {
      //console.error('❌ Email sign-in failed:', error);
      //console.error('📋 Error details:', {
      //   code: error?.code,
      //   message: error?.message,
      //   email: email
      // });
      // Preserve the Firebase error code for handling in components
      throw error
    }
  }

  async signInWithGoogle(): Promise<AuthUser> {
    try {
      const result = await signInWithPopup(auth, this.googleProvider)
      const { user } = result

      // Ensure user exists in backend
      await this.ensureUserInBackend(user)

      return this.mapUser(user)
    } catch (error) {
      //console.error('❌ Google sign-in failed:', error);
      //console.error('📋 Error details:', {
      //   code: (error as any)?.code,
      //   message: (error as any)?.message,
      //   customData: (error as any)?.customData,
      //   operationType: (error as any)?.operationType
      // });

      // Log specific Google auth error codes
      if ((error as any)?.code === 'auth/popup-blocked') {
        //console.error('🚫 Popup was blocked by browser');
      } else if ((error as any)?.code === 'auth/popup-closed-by-user') {
        //console.error('👤 User closed the popup');
      } else if ((error as any)?.code === 'auth/unauthorized-domain') {
        //console.error('🌐 Domain not authorized in Firebase Console');
      }

      throw error
    }
  }

  async signOut(): Promise<void> {
    await signOut(auth)
    // Clear backend user flag
    localStorage.removeItem('user_exists_in_backend')
  }

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email)
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Ensure user exists in backend when auth state changes
        await this.ensureUserInBackend(user)
        callback(this.mapUser(user))
      } else {
        callback(null)
      }
    })
  }

  getCurrentUser(): AuthUser | null {
    return auth.currentUser ? this.mapUser(auth.currentUser) : null
  }

  async getIdToken(): Promise<string | null> {
    if (auth.currentUser) {
      try {
        return await auth.currentUser.getIdToken()
      } catch (error) {
        //console.error('Failed to get Firebase ID token:', error);
        return null
      }
    }
    return null
  }

  isAuthenticated(): boolean {
    return !!auth.currentUser
  }

  private mapUser(user: User): AuthUser {
    return {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
    }
  }

  async ensureUserInBackend(user: User): Promise<void> {
    try {
      // First, get the Firebase token
      const token = await user.getIdToken()

      // Check if user already exists in backend
      try {
        await api.getCurrentUser()
        return
      } catch (error) {
        // If 404, user doesn't exist, so we'll create them
        if ((error as any)?.response?.status === 404) {
          // Create user in backend
          await api.createUser({
            uid: user.uid,
            email: user.email || '',
            display_name: user.displayName || undefined,
          })
        } else {
          // Other errors - log but don't fail the auth flow
          //console.error('❌ Error checking user in backend:', error);
        }
      }
    } catch (error) {
      //console.error('❌ Failed to ensure user in backend:', error);
      // Don't throw - we don't want to break the auth flow
    }
  }
}

export const authService = new AuthService()
